import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Incremental rendering for long lists.
 *
 * Renders only the first `step` items, then grows the window as the sentinel
 * element scrolls into view. Keeps DOM size (and memory) low on the low-end
 * Android devices most Makai learners use, without changing the data layer.
 */
export function useIncrementalList<T>(items: T[], step = 24) {
  const [count, setCount] = useState(step);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset the window whenever the underlying list changes (filter/search).
  useEffect(() => {
    setCount(step);
  }, [items, step]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || count >= items.length) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setCount((c) => Math.min(c + step, items.length));
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [count, items.length, step]);

  const visible = useMemo(() => items.slice(0, count), [items, count]);
  const hasMore = count < items.length;

  return { visible, hasMore, sentinelRef, showMore: () => setCount((c) => Math.min(c + step, items.length)) };
}
